import React, { useEffect } from 'react';
import { Provider, ErrorBoundary } from '@rollbar/react';

import { getRollBarEnvironment } from './utils/rollbar';

import Routes from './routes';

import useScript, { ScriptEnvironments } from './hooks/utils/useScript';
import useAuth from './hooks/utils/useAuth';
import { SettingsModal } from './components/modals/settings-modal/settings-modal';
import { CrisisModalGlobal } from './components/modals/crisis-modal/crisis-modal';
import { useSettingsModal } from './contexts/settings-modal-context';
import { useCrisisModal } from './contexts/crisis-modal-context';
import { useFi } from './contexts/fi-context';
import { FiBubble } from './components/fi/fi-bubble';
import { FiIntroMessage } from './components/fi/fi-intro-message';
import { FiPanel } from './components/fi/fi-panel';
import { PeersonaPanel } from './components/peersona/peersona-panel';
import { Features, useFeatureFlag } from './hooks/api/useFeatureFlag';
import { usePeersonaAccess } from './hooks/utils/usePeersonaAccess';

// The Fi bubble is the single floating entry point for the unified
// assistant widget. Peersona conversations enter via PeersonaTile surfaces
// (peer profile, post-audio prompt). When the widget is open, the active
// context (Fi vs. Peersona) decides which panel is rendered — see
// AssistantWidgetContext.

function useFiFeatureEnabled(): boolean {
  const { data, enabled } = useFeatureFlag(Features.FI_CONCIERGE);
  const flagExists = data?.data?.[Features.FI_CONCIERGE] != null;
  if (!flagExists) return true;
  return enabled;
}

const FiModalMinimizeBridge: React.FC = () => {
  const { isModalOpen } = useSettingsModal();
  const { isOpen: crisisModalOpen } = useCrisisModal();
  const { panelOpen, closePanel } = useFi();

  useEffect(() => {
    if (panelOpen && (isModalOpen || crisisModalOpen)) {
      closePanel();
    }
  }, [isModalOpen, crisisModalOpen, panelOpen, closePanel]);

  return null;
};

const App: React.FunctionComponent = (): JSX.Element => {
  const { user } = useAuth();
  const fiEnabled = useFiFeatureEnabled();
  const { featureEnabled: peersonaEnabled } = usePeersonaAccess();
  useScript({
    url: '//js.hs-scripts.com/45646789.js',
    id: 'hs-script-loader',
    environments: [ScriptEnvironments.PRODUCTION],
    crossOrigin: undefined,
  });
  /*
   It's a little easier to just inject the script hotjar provides here.
   we could just build the url, but this script is also pushing items to window._hjSettings
   I can see this being changed in the future, so it might be easier to copy paste this way
  */
  useScript({
    functionText: `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:3927454,hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script'); r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
    id: 'hotjar-script-loader',
    environments: [ScriptEnvironments.PRODUCTION],
    async: true,
    defer: false,
    crossOrigin: '',
    type: null,
    addToHead: true,
  });

  // Use the Hotjar script for development environment separately
  useScript({
    functionText: `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:6496485,hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script'); r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
    id: 'hotjar-script-loader',
    environments: [ScriptEnvironments.DEVELOPMENT],
    async: true,
    defer: false,
    crossOrigin: '',
    type: null,
    addToHead: true,
  });

  // Hotjar user identification - only runs when user data is available
  useScript({
    functionText: user?.id
      ? `window.hj('identify','${user.id}',{'uid':'${user.id}','company':'${user?.caller_role?.active_subscription?.package?.code}'});`
      : '// No user ID available',
    id: 'hotjar-identity-api',
    environments: [ScriptEnvironments.PRODUCTION, ScriptEnvironments.DEVELOPMENT],
    async: false,
    defer: true,
    addToHead: true,
  });

  // const history = useHistory();
  // const { pathname } = useLocation();

  // const [showActiveCallModal, setShowActiveCallModal] = useState<boolean>(false);
  // const [shownActiveCall, setShownActiveCall] = useState<(number | undefined)[]>([]);

  // const { data: activeCalls } = useActiveCalls(useUser()?.data?.caller_role_id);
  // const dfMdMedia = 'md';

  const rollbarConfig =
    window.location.origin.indexOf('localhost') !== -1
      ? {}
      : {
          accessToken: process.env.REACT_APP_ROLL_BAR_ACCESS_TOKEN,
          environment: getRollBarEnvironment(),
          code_version: process.env.REACT_APP_CODE_VERSION,
        };

  /**
   * This useEffect defines situations when the user needs to show information about an active call
   * If the user uses a desktop, then a modal with information about an active call will be displayed
   * If the user uses mobile, then the user will be redirected to a page with information about an active call
   * Each of these actions is performed only once per call
   * If the user switches from desktop to mobile and the modal with information about the active call is open,
   * the user will be redirected to the page with information about the active call
   */
  // useEffect(() => {
  //   if (activeCalls?.length && !shownActiveCall.includes(activeCalls?.at(-1)?.id)) {
  //     setShownActiveCall((value) => [...value, activeCalls?.at(-1)?.id]);
  //     if (dfMdMedia && pathname !== ROUTE_PATH.ActiveCall) {
  //       setShowActiveCallModal(true);
  //     } else {
  //       history.push(ROUTE_PATH.ActiveCall);
  //     }
  //   }
  //   if (!dfMdMedia && showActiveCallModal) {
  //     setShowActiveCallModal(false);
  //     history.push(ROUTE_PATH.ActiveCall);
  //   }
  // }, [activeCalls, dfMdMedia, history, pathname, shownActiveCall, showActiveCallModal]);

  return (
    <>
      <Provider config={rollbarConfig}>
        <ErrorBoundary>
          <Routes />
          {/* Don't mount settings during partial onboarding — Profile form defaultValues
              lock in signup-era empty name/phone (KINDLY-1222). Dialog keeps children in DOM
              even when closed, so a premature mount never re-hydrates after profile step. */}
          {user?.caller_role_id && !user?.is_partial && <SettingsModal />}
          {user?.caller_role_id && !user?.is_partial && <CrisisModalGlobal />}
          {fiEnabled && user?.caller_role_id && !user?.is_partial && (
            <>
              <FiModalMinimizeBridge />
              <FiBubble />
              <FiIntroMessage />
              <FiPanel />
            </>
          )}
          {/* Peersona panel. Mounted alongside Fi for logged-in callers.
              The AssistantWidgetContext decides which panel is visible based
              on activeContext; both panels stay mounted so their internal
              state (messages, thread id) is preserved when the user switches
              between Fi and a peer within the same session. Entry is
              tile-based — there is no Peersona bubble. */}
          {peersonaEnabled && user?.caller_role_id && <PeersonaPanel />}
        </ErrorBoundary>
      </Provider>
    </>
  );
};

export default App;
